import React, { useState } from "react";
import { Box, CircularProgress, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { Add, Cancel, Edit, MoreVert, Note } from "@mui/icons-material";
import { observer } from "mobx-react";
import { AppointmentDto } from "../../../api-client";
import WarningDialog from "../../../atoms/WarningDialog";
import AppointmentEditCard from "../../../components/ScheduleOverview/Appointments/AppointmentEditCard";
import EditCancellationDialog from "../../../components/ScheduleOverview/Appointments/CancelAppointmentEditCard";
import CreateTicketForm from "../../../components/Ticketing/TicketEditCreate/createTicketForm";
import { TerminViewConstants } from "../../../components/PatientOverview/TerminView";
import { CopyAppointmentDialog } from "../../CopyAppointmentDialog";
import AppointmentNotes from "../AppointmentNotes";
import notificationStore from "../../../stores/Notification/notificationStore";
import {
  useCancellationOfCancelledAppointment,
  useCopyNewAppointment,
  useDeleteAppointment,
} from "../../../stores/Appointment/appointment.mutation";
import { slotTherapy } from "../../../helpers/slotTherapy";

interface ShelfAppointmentMenuProps {
  appointment: AppointmentDto;
  patientId: number;
  /** Cancelled-by-therapist appointments get the cancellation menu instead of the unscheduled one. */
  isCancelled: boolean;
}

/**
 * Kebab menu for appointment cards on the calendar shelf. Mirrors the menus on the
 * Termine page: the "Unterminierte Behandlungen" menu (Bearbeiten / löschen / Neuer
 * Termin / Notizen) for open appointments and the "Stornierung" menu (Ausfallen lassen
 * / Ticket erstellen / Stornierungsgrund bearbeiten) for cancelled ones. The component
 * is self-contained — it owns its dialog state and mutations so it can be dropped onto
 * any shelf card.
 */
const ShelfAppointmentMenu: React.FC<ShelfAppointmentMenuProps> = observer(
  ({ appointment, patientId, isCancelled }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    // Unscheduled dialogs
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [showNotesDialog, setShowNotesDialog] = useState(false);

    // Cancelled dialogs
    const [cancelReasonDialogOpen, setCancelReasonDialogOpen] = useState(false);
    const [openTicket, setOpenTicket] = useState(false);
    const [ticketData, setTicketData] = useState<any>(null);
    const [resolving, setResolving] = useState(false);

    const { mutateAsync: deleteAppointment, isPending: isDeleting } = useDeleteAppointment();
    const { mutateAsync: copyAppointment } = useCopyNewAppointment();
    const { mutate: cancellationOfCancelledAppointment } = useCancellationOfCancelledAppointment();

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => setAnchorEl(null);

    // --- Unscheduled actions -------------------------------------------------
    const handleCopy = () => {
      if (!appointment.id) {
        notificationStore.showMessage("Kein Termin zum Kopieren gefunden", "error");
        return;
      }
      // With a single timeslot there is nothing to choose, so skip the dialog and copy directly.
      if ((appointment.timeSlots?.length ?? 0) <= 1) {
        const timeSlotIds = appointment.timeSlots?.map((slot: any) => slot.id).filter(Boolean);
        copyAppointment({ appointmentId: appointment.id, isContinuous: false, patientId, timeSlotIds });
        return;
      }
      setCopyDialogOpen(true);
    };

    const handleDelete = async () => {
      if (isDeleting || !appointment.id) return;
      try {
        await deleteAppointment({ appointmentId: appointment.id, patientId });
      } catch (error) {
        console.error("Error deleting appointment:", error);
      } finally {
        setDeleteDialogOpen(false);
      }
    };

    // --- Cancelled actions ---------------------------------------------------
    const resolveCancelledAppointment = () => {
      setResolving(true);
      try {
        cancellationOfCancelledAppointment({
          appointmentId: (appointment as any).cancelledAppointmentId,
          resolved: true,
          patientId,
        });
      } catch (error) {
        console.error("Error resolving cancelled appointment:", error);
      } finally {
        setResolving(false);
        handleMenuClose();
      }
    };

    const openTicketForm = () => {
      const rxData: any[] = [];
      let ticketPatient: any;

      appointment.timeSlots?.forEach((timeSlot: any) => {
        const ticketPatientId =
          timeSlot?.therapyRx?.rx?.rxNumber?.split("-")[0] || timeSlot.therapyRx?.rx?.patient?.id;
        ticketPatient = {
          patientId: ticketPatientId,
          patientData: timeSlot.therapyRx?.rx?.patient,
        };
        rxData.push({
          therapyRx: {
            rx: { rxNumber: timeSlot.therapyRx?.rx?.rxNumber },
            therapy: slotTherapy(timeSlot),
          },
          user: timeSlot.user,
        });
      });

      setTicketData({
        appointmentId: appointment.id,
        appointment: { timeSlots: rxData },
        patientId: ticketPatient?.patientId,
        patient: ticketPatient?.patientData,
      });
      setOpenTicket(true);
    };

    return (
      <>
        <IconButton
          aria-label="Termin-Aktionen"
          aria-controls={`shelf-menu-${appointment.id}`}
          aria-haspopup="true"
          size="small"
          onClick={handleMenuOpen}
          sx={{ p: 0.25 }}
        >
          <MoreVert sx={{ fontSize: 18 }} />
        </IconButton>
        <Menu
          id={`shelf-menu-${appointment.id}`}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          {isCancelled
            ? [
                <MenuItem key="resolve" onClick={resolveCancelledAppointment}>
                  <Tooltip title="Ausfallen lassen">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {resolving ? (
                        <CircularProgress size={20} sx={{ mr: 1, color: "black" }} />
                      ) : (
                        <Cancel className="cancel-icon" />
                      )}
                      Ausfallen lassen
                    </div>
                  </Tooltip>
                </MenuItem>,
                <MenuItem
                  key="ticket"
                  onClick={() => {
                    openTicketForm();
                    handleMenuClose();
                  }}
                >
                  <Note /> Ticket erstellen
                </MenuItem>,
                <MenuItem
                  key="reason"
                  onClick={() => {
                    setCancelReasonDialogOpen(true);
                    handleMenuClose();
                  }}
                >
                  <Edit /> Stornierungsgrund bearbeiten
                </MenuItem>,
              ]
            : [
                <MenuItem
                  key="edit"
                  onClick={() => {
                    setEditDialogOpen(true);
                    handleMenuClose();
                  }}
                >
                  <Edit />
                  Bearbeiten
                </MenuItem>,
                <MenuItem
                  key="delete"
                  onClick={() => {
                    setDeleteDialogOpen(true);
                    handleMenuClose();
                  }}
                >
                  <Tooltip title="löschen">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Cancel className="cancel-icon" />
                      löschen
                    </div>
                  </Tooltip>
                </MenuItem>,
                <MenuItem
                  key="copy"
                  onClick={() => {
                    handleCopy();
                    handleMenuClose();
                  }}
                >
                  <Add /> Neuer Termin
                </MenuItem>,
                <MenuItem
                  key="notes"
                  onClick={() => {
                    setShowNotesDialog(true);
                    handleMenuClose();
                  }}
                >
                  <Edit />
                  Notizen hinzufügen/bearbeiten
                </MenuItem>,
              ]}
        </Menu>

        {/* Dialogs (rendered outside the menu so they survive its close) */}
        {editDialogOpen && (
          <AppointmentEditCard
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            appointment={appointment}
            patientId={patientId}
            isContinous={false}
            isView={TerminViewConstants.UNSCHEDULED_TERMIN}
          />
        )}

        {copyDialogOpen && (
          <CopyAppointmentDialog
            patientId={patientId}
            isOpen={copyDialogOpen}
            onClose={() => setCopyDialogOpen(false)}
            appointment={appointment}
            isContinuous={false}
          />
        )}

        {deleteDialogOpen && (
          <WarningDialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            onContinue={handleDelete}
            title="Termin löschen"
            loading={isDeleting}
            content={
              isDeleting ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CircularProgress size={24} />
                  Löschen läuft, bitte warten...
                </Box>
              ) : (
                "Möchten Sie diesen Termin wirklich löschen?"
              )
            }
          />
        )}

        {showNotesDialog && appointment.id && (
          <AppointmentNotes
            appointmentId={appointment.id}
            showDialog={showNotesDialog}
            onClose={() => setShowNotesDialog(false)}
          />
        )}

        {cancelReasonDialogOpen && (
          <EditCancellationDialog
            open={cancelReasonDialogOpen}
            onClose={() => setCancelReasonDialogOpen(false)}
            appointment={appointment}
            patientId={patientId}
          />
        )}

        {openTicket && (
          <CreateTicketForm
            openTicket={openTicket}
            onCloseTicketForm={() => setOpenTicket(false)}
            data={ticketData}
          />
        )}
      </>
    );
  }
);

ShelfAppointmentMenu.displayName = "ShelfAppointmentMenu";

export default ShelfAppointmentMenu;
